import { z } from "zod";
import { errorCodeSchema, fieldIssueSchema, type ErrorCode } from "@amber/contracts";

const errorBodySchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string().min(1),
    request_id: z.string().min(1),
    issues: z.array(fieldIssueSchema).optional(),
    current_revision: z.number().int().positive().optional(),
    changed_by: z.string().nullable().optional(),
    changed_at: z.string().optional(),
    current: z.unknown().optional(),
  }),
});

export interface FieldIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly requestId: string | null;
  readonly issues: readonly FieldIssue[];
  readonly currentRevision: number | null;
  readonly changedBy: string | null;
  readonly changedAt: string | null;
  readonly current: unknown;

  constructor(init: {
    status: number;
    code: ErrorCode;
    message: string;
    requestId?: string | null;
    issues?: readonly FieldIssue[];
    currentRevision?: number | null;
    changedBy?: string | null;
    changedAt?: string | null;
    current?: unknown;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.issues = init.issues ?? [];
    this.currentRevision = init.currentRevision ?? null;
    this.changedBy = init.changedBy ?? null;
    this.changedAt = init.changedAt ?? null;
    this.current = init.current ?? null;
  }

  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const issue of this.issues) {
      const field = issue.path.replace(/^body\./, "").split(".")[0] ?? issue.path;
      out[field] ??= issue.message;
    }
    return out;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

const FALLBACK_CODES: Record<number, ErrorCode> = {
  400: "validation_failed",
  401: "unauthenticated",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  429: "rate_limited",
  503: "temporarily_unavailable",
};

async function toError(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => null);
  const parsed = errorBodySchema.safeParse(body);
  if (parsed.success) {
    const e = parsed.data.error;
    return new ApiError({
      status: response.status,
      code: e.code,
      message: e.message,
      requestId: e.request_id,
      issues: e.issues ?? [],
      currentRevision: e.current_revision ?? null,
      changedBy: e.changed_by ?? null,
      changedAt: e.changed_at ?? null,
      current: e.current ?? null,
    });
  }
  return new ApiError({
    status: response.status,
    code: FALLBACK_CODES[response.status] ?? "internal",
    message: response.status >= 500 ? "The server couldn't complete the request" : "The request didn't succeed",
  });
}

async function send<S extends z.ZodType>(path: string, schema: S, init: RequestInit): Promise<z.output<S>> {
  let response: Response;
  try {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    response = await fetch(path, { credentials: "same-origin", ...init, headers });
  } catch {
    throw new ApiError({
      status: 0,
      code: "temporarily_unavailable",
      message: "Couldn't reach the server. Check the connection and try again.",
    });
  }
  if (!response.ok) throw await toError(response);
  const body: unknown = await response.json();
  const parsed = schema.safeParse(body);
  if (!parsed.success)
    throw new ApiError({
      status: response.status,
      code: "internal",
      message: "The server sent a response this page doesn't understand",
    });
  return parsed.data;
}

export function apiGet<S extends z.ZodType>(path: string, schema: S, signal?: AbortSignal): Promise<z.output<S>> {
  return send(path, schema, signal ? { method: "GET", signal } : { method: "GET" });
}

export function apiPost<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
  csrfToken: string | null,
): Promise<z.output<S>> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (csrfToken) headers["x-csrf-token"] = csrfToken;
  return send(path, schema, { method: "POST", headers, body: JSON.stringify(body) });
}

export function withQuery(path: string, params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== "") search.set(key, value);
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}
